import { useMemo } from "react";

/**
 * Two-tier product picker. Customers first pick a *product type* (frame,
 * canvas, acrylic stand, coffee mug) and — when the type has multiple sizes —
 * a *size* underneath. Currently only `frame` has multiple sizes (A4 + 8×10);
 * the other types map to a single product.
 *
 * Props:
 *   products        — array from /api/sizes (only active products)
 *   selectedId      — currently selected product id
 *   onSelect(id)    — fired when user picks a different product
 *   priceFor(id)    — () => ({ base, final }) — used to render live prices
 *   coupon          — { code, ... } | null (used only for the "after CODE"
 *                     label on discounted prices)
 */

const TYPE_META = {
  frame:   { label: "Frame",          short: "Premium framed print" },
  canvas:  { label: "Canvas",         short: "Gallery-wrap canvas" },
  acrylic: { label: "Acrylic Stand",  short: "Free-standing display" },
  mug:     { label: "Coffee Mug",     short: "11 oz ceramic mug" },
};

const TYPE_ORDER = ["frame", "canvas", "acrylic", "mug"];

const formatPrice = (n) =>
  typeof n === "number" && !isNaN(n)
    ? `₹${Math.round(n).toLocaleString("en-IN")}`
    : "—";

const ProductPicker = ({ products, selectedId, onSelect, priceFor, coupon, compact = false }) => {
  // Group products by type so we can render a type card + (for frames) a
  // size sub-row underneath.
  const byType = useMemo(() => {
    const groups = {};
    (products || []).forEach((p) => {
      const t = p.product_type || "frame";
      (groups[t] = groups[t] || []).push(p);
    });
    // Sort each group's products by sort_order so A4 sits before 8×10.
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    );
    return groups;
  }, [products]);

  const types = TYPE_ORDER.filter((t) => byType[t]?.length > 0);
  const selected = (products || []).find((p) => p.id === selectedId);
  const selectedType = selected?.product_type || types[0];

  // Cheapest in-group price helper — shown on each type card so customers
  // get a "from ₹X" signal before opening the size sub-picker.
  const minPriceFor = (type) => {
    const arr = byType[type] || [];
    if (arr.length === 0) return null;
    return Math.min(
      ...arr.map((p) => {
        const { final } = priceFor(p.id);
        return final || Infinity;
      })
    );
  };

  // When the user clicks a *type* card we pick the first product of that
  // type (or restore the last-picked product of that type, if available).
  const pickType = (t) => {
    const arr = byType[t] || [];
    if (arr.length === 0) return;
    // Prefer the product flagged is_default within this type; else first.
    const def = arr.find((p) => p.is_default) || arr[0];
    onSelect(def.id);
  };

  // Per-type variant detail shown beneath the product name. Frames return
  // empty because the dedicated frame-size sub-picker below already shows
  // the sizes. Canvas / Acrylic show "W × H in" from the default variant.
  // Mugs show ml (converted from the variant name's "11 oz" → 325 ml) so
  // the India audience gets a familiar unit.
  const formatVariantDetail = (typeKey) => {
    if (typeKey === "frame") return "";
    const arr = byType[typeKey] || [];
    const v = arr.find((p) => p.is_default) || arr[0];
    if (!v) return "";
    if (typeKey === "mug") {
      const m = (v.name || "").match(/(\d+(?:\.\d+)?)\s*oz/i);
      const oz = m ? parseFloat(m[1]) : null;
      if (oz) return `${Math.round(oz * 29.5735)} ml`;
      return v.name || "";
    }
    if (v.width_inches && v.height_inches) {
      const fmt = (n) =>
        Number.isInteger(n) ? `${n}` : n.toFixed(1).replace(/\.0$/, "");
      return `${fmt(v.width_inches)} × ${fmt(v.height_inches)} in`;
    }
    return "";
  };

  return (
    <div data-testid="product-picker">
      <p className="label-uppercase mb-3 text-[#6B6B6B]">Choose product</p>
      <div
        className={
          compact
            ? "grid grid-cols-2 gap-2 mb-5"
            : "grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
        }
        data-testid="product-picker-types"
      >
        {types.map((t) => {
          const meta = TYPE_META[t] || { label: t, short: "" };
          const isActive = selectedType === t;
          const minP = minPriceFor(t);
          const detail = formatVariantDetail(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => pickType(t)}
              className={`text-left ${compact ? "p-3" : "p-4"} border transition-colors ${
                isActive
                  ? "border-[#C8A27A] bg-[#FAF6EF]"
                  : "border-[#EAE5DF] hover:border-[#C8A27A]/60 bg-card"
              }`}
              data-testid={`product-type-${t}`}
            >
              <p className={`serif ${compact ? "text-base" : "text-lg"} text-[#1A1A1A]`}>
                {meta.label}
              </p>
              {!compact && (
                <p className="text-xs text-[#6B6B6B] mt-0.5">{meta.short}</p>
              )}
              {detail && (
                <p
                  className={`text-xs text-[#6B6B6B] ${compact ? "mt-0.5" : "mt-1"}`}
                  data-testid={`product-type-${t}-detail`}
                >
                  {detail}
                </p>
              )}
              {minP !== null && minP !== Infinity && (
                <p className={`text-xs text-[#9B6A2D] ${compact ? "mt-1" : "mt-2"}`}>
                  from {formatPrice(minP)}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Size sub-row — only visible when the active type has more than one
          product. Today this is only `frame` (A4 + 8×10). */}
      {(byType[selectedType] || []).length > 1 && (
        <div data-testid="product-picker-sizes">
          <p className="label-uppercase mb-3 text-[#6B6B6B]">
            Choose {TYPE_META[selectedType]?.label?.toLowerCase() || "size"} size
          </p>
          <div className="grid grid-cols-2 gap-3">
            {(byType[selectedType] || []).map((p) => {
              const { base, final } = priceFor(p.id);
              const discounted = coupon && final < base;
              const isSel = p.id === selectedId;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelect(p.id)}
                  className={`text-left p-3 border transition-colors ${
                    isSel
                      ? "border-[#C8A27A] bg-[#FAF6EF]"
                      : "border-[#EAE5DF] hover:border-[#C8A27A]/60 bg-card"
                  }`}
                  data-testid={`product-size-${p.id}`}
                >
                  <p className="font-medium text-[#1A1A1A]">{p.name}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-base font-bold text-[#1A1A1A]">
                      {formatPrice(final)}
                    </span>
                    {discounted && (
                      <span className="text-xs text-[#9A9A9A] line-through">
                        {formatPrice(base)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPicker;
export { TYPE_META, TYPE_ORDER };
